/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { DragLayer as dragLayer } from 'react-dnd';
import { withContext } from '@ba-ui-toolkit/context';
import { lodash } from '@ba-ui-toolkit/utils';

const { omit } = lodash;

@dragLayer(monitor => ({
	item: monitor.getItem(),
	currentOffset: monitor.getClientOffset(),
	isDragging: monitor.isDragging(),
	canDrop: () =>
		monitor.isDragging() &&
		monitor
			.getTargetIds()
			.some(targetId => monitor.isOverTarget(targetId) && monitor.canDropOnTarget(targetId)),
}))
export class DragLayer extends Component {
	static propTypes = {
		item: PropTypes.object,
		isDragging: PropTypes.bool.isRequired,
		currentOffset: PropTypes.object,
		onBeginDrag: PropTypes.func.isRequired,
		onEndDrag: PropTypes.func.isRequired,
		canDrop: PropTypes.func.isRequired,
	};

	componentWillReceiveProps(nextProps) {
		if (nextProps.isDragging && !this.props.isDragging) {
			nextProps.onBeginDrag();
		} else if (!nextProps.isDragging && this.props.isDragging) {
			nextProps.onEndDrag();
		}
	}

	render() {
		const { currentOffset, item, isDragging, canDrop } = this.props;

		if (!currentOffset || !item || !isDragging || !item.dragLayerRenderer) {
			return null;
		}

		return item.dragLayerRenderer({ itemProps: item, droppable: canDrop(), currentOffset });
	}
}

export default function withDragLayer(WrappedComponent) {
	@withContext('theme')
	class WithDragLayer extends Component {
		static propTypes = {
			theme: PropTypes.string,
			dragLayerForwardRef: PropTypes.any,
		};
		componentWillMount() {
			const dragLayerContainer = document.createElement('div');
			dragLayerContainer.hidden = true;
			document.body.appendChild(dragLayerContainer);
			this._dragLayerContainer = dragLayerContainer;
		}

		componentWillUnmount() {
			if (this._dragLayerContainer) {
				ReactDOM.unmountComponentAtNode(this._dragLayerContainer);
				this._dragLayerContainer.parentNode.removeChild(this._dragLayerContainer);
				this._dragLayerContainer = null;
			}
		}

		_onBeginDrag = () => {
			this._dragLayerContainer.hidden = false;
		};

		_onEndDrag = () => {
			this._dragLayerContainer.hidden = true;
		};

		render() {
			const { dragLayerForwardRef, ...other } = this.props;

			const passThroughProps = omit(other, 'theme');

			return [
				<WrappedComponent key="0" {...passThroughProps} ref={dragLayerForwardRef} />,
				ReactDOM.createPortal(
					<DragLayer onBeginDrag={this._onBeginDrag} onEndDrag={this._onEndDrag} />,
					this._dragLayerContainer
				),
			];
		}
	}

	return React.forwardRef((props, ref) => <WithDragLayer {...props} dragLayerForwardRef={ref} />);
}
